import React, { useState } from 'react';
import axios from 'axios';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Trash2, AlertTriangle, Loader2, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DangerZoneTab({ language }) {
  const isLV = language === 'lv';
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const PHRASE = 'RESET_PRODUCTION';

  const handleReset = async () => {
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/reset-production-data`, { confirm: PHRASE });
      setResult(res.data);
      setConfirmText('');
    } catch (e) {
      setError(formatApiError(e, (isLV ? 'Kļūda. Mēģini vēlreiz.' : 'Error. Try again.')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="danger-zone-tab">
      <div className="bg-white rounded-2xl border-2 border-red-200 overflow-hidden">
        <div className="bg-red-50 p-5 border-b border-red-200 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-outfit font-bold text-lg text-red-900">
              {isLV ? 'Demo datu notīrīšana' : 'Clear demo data'}
            </h2>
            <p className="text-sm text-red-800 mt-1">
              {isLV
                ? 'Dzēš visus demo lietotājus, sludinājumus, pasūtījumus, atsauksmes un nulles visas maciņa bilances. Saglabā tikai Tavu admin kontu. Šo darbību nevar atsaukt!'
                : 'Deletes all demo users, listings, orders, reviews and zeroes all wallets. Keeps only your admin account. This action cannot be undone!'}
            </p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4">
            <p className="text-sm text-[#121212] font-medium mb-2">
              {isLV ? 'Šī darbība dzēsīs:' : 'This action will delete:'}
            </p>
            <ul className="text-sm text-[#5C5C5C] space-y-1 list-disc pl-5">
              <li>{isLV ? 'Visus ne-admin lietotājus' : 'All non-admin users'}</li>
              <li>{isLV ? 'Visus sludinājumus, pasūtījumus, grozus, favorītus' : 'All listings, orders, carts, favourites'}</li>
              <li>{isLV ? 'Visas ziņas un atsauksmes' : 'All messages and reviews'}</li>
              <li>{isLV ? 'Visus paziņojumus, verifikācijas pieprasījumus, izņemšanas' : 'All notifications, verification requests, withdrawals'}</li>
              <li>{isLV ? 'Maciņa darījumus un komisijas' : 'Wallet transactions and commissions'}</li>
              <li>{isLV ? 'Nullē visus maciņu balansus' : 'Zeroes all wallet balances'}</li>
            </ul>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="danger-error">
              {error}
            </div>
          )}

          {result && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm" data-testid="danger-success">
              <p className="font-medium mb-1">
                {isLV ? 'Veiksmīgi notīrīts!' : 'Successfully cleared!'}
              </p>
              <p>{isLV ? 'Palikušie lietotāji:' : 'Remaining users:'} <strong>{result.users_remaining}</strong></p>
              <p>{isLV ? 'Admin:' : 'Admin:'} <strong>{result.admin_email}</strong></p>
            </div>
          )}

          {!result && (
            <>
              <div className="space-y-2">
                <label className="text-sm text-[#121212] font-medium block">
                  {isLV ? 'Lai apstiprinātu, ieraksti:' : 'To confirm, type:'}{' '}
                  <code className="px-2 py-0.5 bg-[#FAFAFA] border border-[#E5E4E0] rounded text-red-700 font-mono">{PHRASE}</code>
                </label>
                <Input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={PHRASE}
                  className="h-11 bg-white border-red-200 focus:border-red-400"
                  data-testid="danger-confirm-input"
                />
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    disabled={confirmText !== PHRASE || loading}
                    className="w-full h-11 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    data-testid="danger-reset-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isLV ? 'Tīra...' : 'Clearing...'}</>
                    ) : (
                      <><Trash2 className="w-4 h-4 mr-2" /> {isLV ? 'Dzēst visus demo datus' : 'Delete all demo data'}</>
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {isLV ? 'Vai tiešām vēlies turpināt?' : 'Are you sure you want to proceed?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {isLV
                        ? 'Visi demo dati tiks neatgriezeniski dzēsti. Tavs admin konts paliks neskarts.'
                        : 'All demo data will be permanently deleted. Your admin account will remain.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="danger-cancel-btn">
                      {isLV ? 'Atcelt' : 'Cancel'}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleReset}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="danger-confirm-btn"
                    >
                      {isLV ? 'Jā, dzēst' : 'Yes, delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DangerZoneTab;
